import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Button, Group, Stack } from '@repletoryreach/sdk-ui';
import { Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * A website chat's name, greeting and allowed websites, changed in place
 * (owner, 22 September 2026). The site key stays, so the snippet already on a
 * website keeps working and the change shows on its next page load. Before
 * this, a greeting could only be changed by deleting the chat and creating it
 * again, which gave it a new key and broke every copy of the old snippet.
 */
export function EditWebSiteModal({ site, onClose }: { site: flows.WebSite; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [name, setName] = useState(site.name);
  const [greeting, setGreeting] = useState(site.greeting ?? '');
  const [origins, setOrigins] = useState((site.allowed_origins ?? []).join('\n'));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => flows.updateWebSite(site.id, {
      name: name.trim(),
      greeting: greeting.trim() || null,
      allowed_origins: origins.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean)
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['web-sites'] });
      notifySuccess(t('flows.connect.siteSaved'), t('flows.connect.website'));
      onClose();
    },
    // The service says exactly what it refused ("Give each website as its address only, ..."): show it.
    onError: (e) => setError(e instanceof Error ? e.message : t('common.unknownError'))
  });

  const nameOk = name.trim().length > 0 && name.trim().length <= 120;

  return (
    <Modal opened onClose={onClose} title={t('flows.connect.editSiteTitle')} size="md">
      <Stack gap="md">
        <TextInput
          label={t('flows.connect.siteName')}
          description={t('flows.connect.siteNameHint')}
          value={name}
          maxLength={120}
          onChange={(e) => { setName(e.currentTarget.value); setError(null); }}
        />
        <Textarea
          label={t('flows.connect.greeting')}
          description={t('flows.connect.greetingHint')}
          value={greeting}
          maxLength={500}
          autosize
          minRows={3}
          onChange={(e) => { setGreeting(e.currentTarget.value); setError(null); }}
        />
        <Textarea
          label={t('flows.connect.allowedSites')}
          description={t('flows.connect.allowedSitesHint')}
          value={origins}
          autosize
          minRows={2}
          placeholder={'https://www.example.co.za' /* i18n-ignore: an example address */}
          onChange={(e) => { setOrigins(e.currentTarget.value); setError(null); }}
        />
        {error && <Alert color="error">{error}</Alert>}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>{t('action.cancel')}</Button>
          <Button disabled={!nameOk} loading={save.isPending} onClick={() => save.mutate()}>{t('action.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
